<!--
더미데이터 생성기 — 시더 선택 + 파라미터 입력 + 디스패치 + 폴링
-->
<template>
    <div class="dummy-data-wrap">

        <!-- ====== 주요 개념 (아코디언) ====== -->
        <ul class="glossary-accordion" uk-accordion>
            <li>
                <a class="uk-accordion-title" href="#">
                    <span class="accordion-toggle-icon" uk-icon="icon: chevron-right; ratio: 0.8"></span>
                    주요 개념
                </a>
                <div class="uk-accordion-content">
                    <table class="glossary-table">
                        <tbody>
                            <tr>
                                <th>Faker</th>
                                <td>실제처럼 보이는 가짜 값을 생성하는 PHP 라이브러리. 이름, 주소, 전화번호 등</td>
                            </tr>
                            <tr>
                                <th>Factory</th>
                                <td>모델 1건의 기본 속성값을 정의하는 클래스. Faker를 내부에서 사용 <code>UserFactory</code> <code>HouseFactory</code></td>
                            </tr>
                            <tr>
                                <th>Seeder</th>
                                <td>Factory를 조합하여 비즈니스 시나리오 데이터를 구성하는 클래스 <code>AgentMemberSeeder</code></td>
                            </tr>
                            <tr>
                                <th>SeederRegistry</th>
                                <td>시더 등록/조회 중앙 저장소. 등록하면 웹 UI와 CLI(<code>dummy:seed</code>)에 자동 노출</td>
                            </tr>
                            <tr>
                                <th>Queue Job</th>
                                <td>시더를 비동기로 실행하는 Laravel Job. 타임아웃 없이 대량 데이터 생성 가능</td>
                            </tr>
                            <tr>
                                <th>batch_job_progresses</th>
                                <td>진행률 추적 테이블. 웹 UI가 2초 간격으로 폴링하여 상태를 표시</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="glossary-flow">
                        <span>Faker</span> <span class="flow-arrow">&rarr;</span>
                        <span>Factory</span> <span class="flow-arrow">&rarr;</span>
                        <span>Seeder</span> <span class="flow-arrow">&rarr;</span>
                        <span>Queue Job</span> <span class="flow-arrow">&rarr;</span>
                        <span>batch_job_progresses</span> <span class="flow-arrow">&rarr;</span>
                        <span>웹 폴링</span>
                    </div>
                    <p class="glossary-doc-link">
                        상세 구조 및 시더 추가 가이드는 <code>/doc/더미데이터-생성기.md</code> 문서를 참고하세요.
                    </p>
                </div>
            </li>
        </ul>

        <!-- ====== 메인 카드 ====== -->
        <div class="main-card">

            <!-- 로딩중 -->
            <div v-if="loading" class="uk-text-center uk-padding">
                <div uk-spinner="ratio: 2"></div>
                <p class="uk-text-muted uk-margin-small-top">시더 목록을 불러오는 중...</p>
            </div>

            <!-- 시더 목록 로드 완료 -->
            <div v-else>

                <!-- ====== 입력 영역 (ready 상태) ====== -->
                <div v-show="jobState === 'ready'">

                    <!-- 시더가 없는 경우 -->
                    <div v-if="seeders.length === 0" class="uk-alert uk-alert-warning">
                        등록된 시더가 없습니다. DummySeederRegistry에 시더를 등록하세요.
                    </div>

                    <!-- 시더가 있는 경우 -->
                    <div v-else>

                        <!-- 시더 선택 — 시나리오별 파이프라인 -->
                        <div class="pipeline-wrap">
                            <div class="pipeline-label">시나리오 선택</div>
                            <div
                                v-for="(scenario, sIdx) in pipelineScenarios"
                                :key="sIdx"
                                class="pipeline-scenario"
                            >
                                <div class="pipeline-scenario-label">{{ scenario.label }}</div>
                                <div class="pipeline">
                                    <div
                                        v-for="(node, nIdx) in scenario.chain"
                                        :key="node.type"
                                        class="pipeline-node"
                                        :class="{
                                            'pipeline-selected': selectedType === node.type,
                                            'pipeline-ancestor': isAncestorOf(node.type, selectedType),
                                        }"
                                        @click="selectSeederByType(node.type)"
                                    >
                                        <span class="pipeline-step">{{ nIdx + 1 }}</span>
                                        <span class="pipeline-node-label">{{ node.label }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 시더 설명 (있는 경우) -->
                        <div v-if="selectedSeeder && selectedSeeder.description" class="seeder-description form-content">
                            <i class="material-icons" style="font-size:14px; vertical-align:middle; margin-right:4px;">info_outline</i>
                            {{ selectedSeeder.description }}
                        </div>

                        <!-- 파라미터 입력 폼 (시더 선택 시) — 시더별 그룹 -->
                        <div v-if="selectedSeeder" class="form-content">
                            <div class="param-section">
                                <div class="param-section-title">파라미터</div>
                                <div
                                    v-for="(group, gIdx) in groupedParameters"
                                    :key="gIdx"
                                    class="param-group"
                                    :class="{ 'param-group-empty': !group.params.length }"
                                >
                                    <div class="param-group-header">
                                        <span class="param-group-number">{{ gIdx + 1 }}</span>
                                        {{ group.label }}
                                        <span v-if="!group.params.length" class="param-group-auto">자동 처리</span>
                                    </div>
                                    <div
                                        v-for="param in group.params"
                                        :key="param.name"
                                        class="form-group"
                                        :class="{ 'form-group-wide': param.type === 'member-select' }"
                                    >
                                        <label class="form-label">
                                            {{ param.label }}
                                            <span v-if="param.required" class="required-mark">*</span>
                                        </label>

                                        <!-- number 타입 -->
                                        <input
                                            v-if="param.type === 'number'"
                                            class="uk-input uk-form-small"
                                            type="number"
                                            :min="param.min !== undefined ? param.min : 1"
                                            v-model.number="paramValues[param.name]"
                                            :placeholder="param.default !== undefined && param.default !== '' ? '기본값: ' + param.default : ''"
                                        />

                                        <!-- text 타입 -->
                                        <input
                                            v-if="param.type === 'text'"
                                            class="uk-input uk-form-small"
                                            type="text"
                                            v-model.trim="paramValues[param.name]"
                                        />

                                        <!-- region-select 타입 (검색 셀렉트) -->
                                        <div v-if="param.type === 'region-select'" class="region-select-wrap">
                                            <multiselect
                                                :value="selectedRegionObj(param.name)"
                                                @input="onRegionSelect($event, param.name)"
                                                :options="regions"
                                                :searchable="true"
                                                :show-labels="false"
                                                track-by="id"
                                                label="name"
                                                placeholder="센터를 선택하세요"
                                            >
                                                <template slot="singleLabel" slot-scope="{ option }">
                                                    <img v-if="option.flag16" :src="option.flag16" class="region-flag" />
                                                    <span>{{ option.name }}</span>
                                                </template>
                                                <template slot="option" slot-scope="{ option }">
                                                    <img v-if="option.flag16" :src="option.flag16" class="region-flag" />
                                                    <span>{{ option.name }}</span>
                                                </template>
                                                <template slot="noResult">검색 결과 없음</template>
                                            </multiselect>
                                        </div>

                                        <!-- member-select 타입 (다중 셀렉트) -->
                                        <div v-if="param.type === 'member-select'" class="member-select-wrap">
                                            <multiselect
                                                v-model="paramValues[param.name]"
                                                :options="members"
                                                :multiple="true"
                                                :searchable="true"
                                                :close-on-select="false"
                                                :show-labels="false"
                                                track-by="id"
                                                label="display_label"
                                                placeholder="회원사를 선택하세요"
                                                :loading="membersLoading"
                                            >
                                                <template slot="option" slot-scope="{ option }">
                                                    <span>{{ option.nick }}</span>
                                                    <span class="member-option-detail">
                                                        ({{ option.mailbox }})
                                                        <span :class="'payment-badge-' + option.payment_type">
                                                            {{ option.payment_type === 'card' ? '카드' : '예치금' }}
                                                        </span>
                                                    </span>
                                                </template>
                                                <template slot="tag" slot-scope="{ option, remove }">
                                                    <span class="member-tag">
                                                        {{ option.nick }}
                                                        <span class="member-tag-payment" :class="'payment-' + option.payment_type">
                                                            {{ option.payment_type === 'card' ? '카드' : '예치금' }}
                                                        </span>
                                                        <span class="member-tag-remove" @click="remove(option)">&times;</span>
                                                    </span>
                                                </template>
                                                <template slot="noResult">검색 결과 없음</template>
                                                <template slot="noOptions">센터를 먼저 선택하세요</template>
                                            </multiselect>
                                        </div>

                                        <!-- select 타입 (드롭다운) -->
                                        <select
                                            v-if="param.type === 'select'"
                                            class="uk-select uk-form-small"
                                            v-model.number="paramValues[param.name]"
                                        >
                                            <option
                                                v-for="opt in param.options"
                                                :key="opt.value"
                                                :value="opt.value"
                                            >{{ opt.label }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- 실행 버튼 -->
                            <div class="form-action">
                                <button
                                    class="btn-generate"
                                    type="button"
                                    @click="dispatch"
                                >
                                    <span uk-icon="icon: play; ratio: 1"></span>
                                    더미데이터 생성
                                </button>
                            </div>
                        </div>

                    </div>
                </div>


                <!-- ====== 작업 상태 패널 (ready 이외 모든 상태) ====== -->
                <div v-if="jobState !== 'ready'" class="job-status-panel" :class="'job-status-' + jobState">

                    <!-- 헤더: 상태 아이콘 + 뱃지 + 시더명 -->
                    <div class="job-panel-header">
                        <span class="job-state-badge" :class="'state-' + jobState">
                            <i v-if="jobState === 'pending'" class="material-icons badge-icon">schedule</i>
                            <span v-if="jobState === 'doing'" uk-spinner="ratio: 0.4"></span>
                            <i v-if="jobState === 'done'" class="material-icons badge-icon">check_circle</i>
                            <i v-if="jobState === 'failed'" class="material-icons badge-icon">error</i>
                            {{ jobStateLabel }}
                        </span>
                        <span v-if="activeJobInfo" class="job-panel-seeder">{{ activeJobInfo.label }}</span>
                    </div>

                    <!-- 파라미터 상세 -->
                    <div v-if="activeJobInfo && activeJobInfo.params.length" class="job-panel-params">
                        <table class="job-params-table">
                            <tr v-for="(p, idx) in activeJobInfo.params" :key="idx">
                                <th>{{ p.label }}</th>
                                <td>
                                    <img v-if="p.flag" :src="p.flag" class="region-flag" />
                                    <span v-if="p.value !== '' && p.value !== null">{{ p.value }}</span>
                                    <span v-else class="param-empty">-</span>
                                </td>
                            </tr>
                        </table>
                    </div>

                    <!-- 본문: 상태별 콘텐츠 -->
                    <div class="job-panel-body">

                        <!-- 대기중 -->
                        <div v-if="jobState === 'pending'" class="uk-text-center">
                            <p class="job-status-msg">
                                서버에서 작업을 준비하고 있습니다.
                            </p>
                        </div>

                        <!-- 실행중 -->
                        <div v-if="jobState === 'doing'">
                            <div class="progress-section">
                                <div class="progress-count">
                                    <span class="progress-current">{{ processedCount }}</span>
                                    <span class="progress-divider">/</span>
                                    <span class="progress-total">{{ totalCount }}</span>
                                    <span v-if="progressPercent !== null" class="progress-pct">({{ progressPercent }}%)</span>
                                </div>
                                <progress
                                    class="uk-progress progress-bar"
                                    :value="processedCount"
                                    :max="totalCount"
                                ></progress>
                                <p class="job-status-msg">
                                    브라우저를 닫아도 서버에서 처리가 계속됩니다.
                                </p>
                            </div>
                        </div>

                        <!-- 완료 -->
                        <div v-if="jobState === 'done'" class="uk-text-center">
                            <div class="progress-section">
                                <div class="progress-count">
                                    <span class="progress-current">{{ processedCount }}</span>
                                    <span class="progress-divider">/</span>
                                    <span class="progress-total">{{ totalCount }}</span>
                                    <span v-if="progressPercent !== null" class="progress-pct">({{ progressPercent }}%)</span>
                                </div>
                                <progress
                                    class="uk-progress progress-bar"
                                    :value="processedCount"
                                    :max="totalCount"
                                ></progress>
                            </div>
                            <button
                                class="uk-button uk-button-default uk-button-small reset-btn"
                                type="button"
                                @click="reset"
                            >
                                <span uk-icon="icon: refresh; ratio: 0.7"></span>
                                다시 생성하기
                            </button>
                        </div>

                        <!-- 실패 -->
                        <div v-if="jobState === 'failed'" class="uk-text-center">
                            <p v-if="errorMessage" class="error-msg">
                                {{ errorMessage }}
                            </p>
                            <div v-if="totalCount > 0" class="progress-section">
                                <div class="progress-count progress-count-sm">
                                    {{ processedCount }} / {{ totalCount }}
                                </div>
                                <progress
                                    class="uk-progress progress-bar"
                                    :value="processedCount"
                                    :max="totalCount"
                                ></progress>
                            </div>
                            <button
                                class="uk-button uk-button-default uk-button-small reset-btn"
                                type="button"
                                @click="reset"
                            >
                                <span uk-icon="icon: refresh; ratio: 0.7"></span>
                                다시 시도하기
                            </button>
                        </div>
                    </div>

                    <!-- 푸터: 폴링 타임스탬프 -->
                    <p v-if="lastPolledAt" class="poll-timestamp">
                        최근 확인: {{ lastPolledAt }}
                    </p>
                </div>

                <!-- ====== 새 작업 추가 (대기중/실행중일 때) ====== -->
                <div v-if="jobState === 'pending' || jobState === 'doing'" class="new-job-section">

                    <!-- 접힌 상태: 버튼만 -->
                    <div v-if="!showNewJobForm" class="uk-text-center">
                        <button
                            class="uk-button uk-button-text uk-button-small"
                            type="button"
                            @click="showNewJobForm = true"
                        >
                            <span uk-icon="icon: plus-circle; ratio: 0.8"></span>
                            새 작업 추가
                        </button>
                    </div>

                    <!-- 펼친 상태: 입력 폼 -->
                    <div v-else class="form-content">
                        <div class="pipeline-wrap">
                            <div class="pipeline-label">시나리오 선택</div>
                            <div
                                v-for="(scenario, sIdx) in pipelineScenarios"
                                :key="sIdx"
                                class="pipeline-scenario"
                            >
                                <div class="pipeline-scenario-label">{{ scenario.label }}</div>
                                <div class="pipeline">
                                    <div
                                        v-for="(node, nIdx) in scenario.chain"
                                        :key="node.type"
                                        class="pipeline-node"
                                        :class="{
                                            'pipeline-selected': selectedType === node.type,
                                            'pipeline-ancestor': isAncestorOf(node.type, selectedType),
                                        }"
                                        @click="selectSeederByType(node.type)"
                                    >
                                        <span class="pipeline-step">{{ nIdx + 1 }}</span>
                                        <span class="pipeline-node-label">{{ node.label }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="selectedSeeder" class="param-section">
                            <div class="param-section-title">파라미터</div>
                            <div
                                v-for="(group, gIdx) in groupedParameters"
                                :key="gIdx"
                                class="param-group"
                                :class="{ 'param-group-empty': !group.params.length }"
                            >
                                <div class="param-group-header">
                                    <span class="param-group-number">{{ gIdx + 1 }}</span>
                                    {{ group.label }}
                                    <span v-if="!group.params.length" class="param-group-auto">자동 처리</span>
                                </div>
                                <div
                                    v-for="param in group.params"
                                    :key="param.name"
                                    class="form-group"
                                >
                                    <label class="form-label">
                                        {{ param.label }}
                                        <span v-if="param.required" class="required-mark">*</span>
                                    </label>
                                    <input
                                        v-if="param.type === 'number'"
                                        class="uk-input uk-form-small"
                                        type="number"
                                        :min="param.min !== undefined ? param.min : 1"
                                        v-model.number="paramValues[param.name]"
                                        :placeholder="param.default !== undefined && param.default !== '' ? '기본값: ' + param.default : ''"
                                    />
                                    <input
                                        v-if="param.type === 'text'"
                                        class="uk-input uk-form-small"
                                        type="text"
                                        v-model.trim="paramValues[param.name]"
                                    />
                                    <div v-if="param.type === 'region-select'" class="region-select-wrap">
                                        <multiselect
                                            :value="selectedRegionObj(param.name)"
                                            @input="onRegionSelect($event, param.name)"
                                            :options="regions"
                                            :searchable="true"
                                            :show-labels="false"
                                            track-by="id"
                                            label="name"
                                            placeholder="센터를 선택하세요"
                                        >
                                            <template slot="singleLabel" slot-scope="{ option }">
                                                <img v-if="option.flag16" :src="option.flag16" class="region-flag" />
                                                <span>{{ option.name }}</span>
                                            </template>
                                            <template slot="option" slot-scope="{ option }">
                                                <img v-if="option.flag16" :src="option.flag16" class="region-flag" />
                                                <span>{{ option.name }}</span>
                                            </template>
                                            <template slot="noResult">검색 결과 없음</template>
                                        </multiselect>
                                    </div>
                                    <div v-if="param.type === 'member-select'" class="member-select-wrap">
                                        <multiselect
                                            v-model="paramValues[param.name]"
                                            :options="members"
                                            :multiple="true"
                                            :searchable="true"
                                            :close-on-select="false"
                                            :show-labels="false"
                                            track-by="id"
                                            label="display_label"
                                            placeholder="회원사를 선택하세요"
                                            :loading="membersLoading"
                                        >
                                            <template slot="option" slot-scope="{ option }">
                                                <span>{{ option.nick }}</span>
                                                <span class="member-option-detail">
                                                    ({{ option.mailbox }})
                                                    <span :class="'payment-badge-' + option.payment_type">
                                                        {{ option.payment_type === 'card' ? '카드' : '예치금' }}
                                                    </span>
                                                </span>
                                            </template>
                                            <template slot="tag" slot-scope="{ option, remove }">
                                                <span class="member-tag">
                                                    {{ option.nick }}
                                                    <span class="member-tag-payment" :class="'payment-' + option.payment_type">
                                                        {{ option.payment_type === 'card' ? '카드' : '예치금' }}
                                                    </span>
                                                    <span class="member-tag-remove" @click="remove(option)">&times;</span>
                                                </span>
                                            </template>
                                            <template slot="noResult">검색 결과 없음</template>
                                            <template slot="noOptions">센터를 먼저 선택하세요</template>
                                        </multiselect>
                                    </div>
                                    <select
                                        v-if="param.type === 'select'"
                                        class="uk-select uk-form-small"
                                        v-model.number="paramValues[param.name]"
                                    >
                                        <option
                                            v-for="opt in param.options"
                                            :key="opt.value"
                                            :value="opt.value"
                                        >{{ opt.label }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div v-if="selectedSeeder" class="form-action">
                            <button
                                class="uk-button uk-button-primary uk-width-1-1"
                                type="button"
                                @click="dispatch"
                            >
                                <span uk-icon="icon: play; ratio: 0.9"></span>
                                더미데이터 생성
                            </button>
                        </div>
                    </div>
                </div>

            </div>

        </div>
        <!-- //메인 카드 -->

        <!-- ====== 최근 작업 이력 ====== -->
        <div v-if="recentJobs.length > 0" class="history-card">
            <div class="history-header">
                <span class="history-title">최근 작업 이력</span>
                <span class="history-count">{{ recentJobs.length }}건</span>
            </div>
            <div class="history-body app-table-normal">
                <table class="uk-table uk-table-small uk-table-divider uk-table-middle uk-margin-remove history-table">
                    <thead>
                        <tr>
                            <th class="uk-text-nowrap">등록일시</th>
                            <th class="uk-text-nowrap uk-text-center">상태</th>
                            <th class="uk-text-nowrap uk-text-center">진행률</th>
                            <th>요청내용</th>
                            <th>처리결과</th>
                            <th class="uk-text-nowrap">완료일시</th>
                            <th>비고</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="job in recentJobs" :key="job.id">
                            <td class="uk-text-nowrap">{{ job.created_at }}</td>
                            <td class="uk-text-center">
                                <span :class="statusBadgeClass(job.status)">
                                    {{ statusLabel(job.status) }}
                                </span>
                            </td>
                            <td class="uk-text-nowrap uk-text-center">
                                {{ job.processed_count }} / {{ job.total_count }}
                            </td>
                            <td class="uk-text-small">
                                <template v-if="job.request_data">
                                    <div v-if="job.request_data.params && job.request_data.params.region_id"
                                         class="request-region">
                                        <img v-if="getRegionFlag(job.request_data.params.region_id)"
                                             :src="getRegionFlag(job.request_data.params.region_id)"
                                             class="region-flag" />
                                        <span>{{ getRegionName(job.request_data.params.region_id) }}</span>
                                    </div>
                                    <div v-if="job.request_data.params && job.request_data.params.count"
                                         class="uk-text-muted">
                                        {{ job.request_data.params.count }}건
                                    </div>
                                </template>
                                <span v-else class="uk-text-muted">-</span>
                            </td>
                            <td class="uk-text-small">
                                <template v-if="job.result_data">
                                    <div v-if="job.result_data.created_users">
                                        <span v-for="(user, idx) in job.result_data.created_users" :key="user.id">
                                            <a :href="'/admin/users/' + user.id"
                                               target="_blank"
                                               class="result-user-link">{{ user.nick }}</a>
                                            <span v-if="idx < job.result_data.created_users.length - 1">, </span>
                                        </span>
                                    </div>
                                    <div v-if="job.result_data.prod_ids && job.result_data.prod_ids.length > 0">
                                        <a :href="buildProdsUrl(job.result_data.prod_ids)"
                                           target="_blank"
                                           class="result-house-link">생성된 하우스({{ job.result_data.houses }})</a>
                                    </div>
                                </template>
                                <span v-else-if="job.status === 3" class="uk-text-muted">-</span>
                            </td>
                            <td class="uk-text-nowrap">{{ job.completed_at || '-' }}</td>
                            <td>
                                <span v-if="job.error_message" class="uk-text-danger">
                                    {{ job.error_message }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

    </div>
</template>

<script>
    import { get_region } from '../../../classes/region-factory';
    import Multiselect from 'vue-multiselect';

    export default {
        components: {
            Multiselect: Multiselect,
        },
        props: {
            csrfToken: String,
        },
        data: function () {
            return {
                // 시더 목록 로딩
                loading: true,

                // 시더 목록 + 센터 목록
                seeders: [],
                regions: [],

                // 선택된 시더
                selectedType: '',

                // 파라미터 입력값
                paramValues: {},

                // 센터별 회원사 목록 (member-select용)
                members: [],
                membersLoading: false,

                /**
                 * 작업 진행상태
                 *  - ready   : 시더 선택 대기 (초기 화면)
                 *  - pending : 작업 대기중 (Job 큐 등록됨, Worker 픽업 전)
                 *  - doing   : 실행중 (Worker가 Job을 픽업하여 처리중)
                 *  - done    : 완료
                 *  - failed  : 실패
                 */
                jobState: 'ready',

                // 폴링용 데이터
                progressId: null,
                pollTimer: null,
                totalCount: 0,
                processedCount: 0,
                errorMessage: null,

                // 최근 작업 이력
                recentJobs: [],

                // 폴링 마지막 조회 시각
                lastPolledAt: null,

                // 대기중/실행중 상태에서 새 작업 입력폼 표시 여부
                showNewJobForm: false,

                // 현재 활성 작업 정보 (pending/doing 상태에서 표시용)
                activeJobInfo: null,
            };
        },
        computed: {
            /**
             * 현재 선택된 시더 객체
             */
            selectedSeeder: function () {
                if (!this.selectedType) return null;
                return _.find(this.seeders, { type: this.selectedType }) || null;
            },

            /**
             * 시더 트리에서 root→leaf 모든 경로를 시나리오별로 반환
             * [{ label: '반품신청', chain: [{type, label}, ...] }, ...]
             */
            pipelineScenarios: function () {
                if (!this.seeders.length) return [];

                // type → seeder 맵
                var map = {};
                this.seeders.forEach(function (s) { map[s.type] = s; });

                // 루트 찾기 (parent_type이 null)
                var root = null;
                this.seeders.forEach(function (s) {
                    if (!s.parent_type) root = s;
                });
                if (!root) return [{ label: '', chain: this.seeders.map(function (s) { return { type: s.type, label: s.label }; }) }];

                // parent_type → children 맵 (1:N)
                var childrenOf = {};
                this.seeders.forEach(function (s) {
                    if (s.parent_type) {
                        if (!childrenOf[s.parent_type]) childrenOf[s.parent_type] = [];
                        childrenOf[s.parent_type].push(s);
                    }
                });

                // DFS로 root→leaf 모든 경로 수집
                var allPaths = [];
                function findPaths(node, path) {
                    path.push({ type: node.type, label: node.label });
                    var children = childrenOf[node.type] || [];
                    if (children.length === 0) {
                        allPaths.push(path.slice());
                    } else {
                        children.forEach(function (child) { findPaths(child, path); });
                    }
                    path.pop();
                }
                findPaths(root, []);

                // 각 경로의 leaf 라벨을 시나리오명으로 사용
                return allPaths.map(function (path) {
                    var leaf = path[path.length - 1];
                    return { label: leaf.label, chain: path };
                });
            },

            /**
             * 시더별 파라미터 그루핑
             */
            groupedParameters: function () {
                if (!this.selectedSeeder) return [];

                // 파이프라인 체인에서 현재 시더까지의 경로를 구한다
                var selectedType = this.selectedType;
                var chain = [];
                var scenario = _.find(this.pipelineScenarios, function (s) {
                    return _.some(s.chain, { type: selectedType });
                });
                if (scenario) {
                    for (var i = 0; i < scenario.chain.length; i++) {
                        chain.push(scenario.chain[i]);
                        if (scenario.chain[i].type === selectedType) break;
                    }
                }

                // 체인의 각 노드별로 그룹을 생성 (파라미터 없는 시더도 빈 그룹으로 포함)
                var params = this.selectedSeeder.parameters;
                var groups = chain.map(function (node) {
                    var nodeParams = params.filter(function (p) {
                        return p.seeder_label === node.label;
                    });
                    return { label: node.label, params: nodeParams };
                });

                return groups;
            },

            /**
             * 현재 작업 상태 라벨
             */
            jobStateLabel: function () {
                var labels = {
                    pending: '대기중',
                    doing: '진행중',
                    done: '완료',
                    failed: '실패',
                };
                return labels[this.jobState] || '';
            },

            /**
             * 진행률 퍼센트 (소수점 없음, null if totalCount=0)
             */
            progressPercent: function () {
                if (!this.totalCount) return null;
                return Math.round((this.processedCount / this.totalCount) * 100);
            },
        },
        watch: {
            'paramValues.region_id': function (newVal) {
                if (newVal) {
                    this.loadMembers(newVal);
                } else {
                    this.members = [];
                }
            },
        },
        methods: {
            /**
             * 시더 목록 로드
             */
            loadSeeders: function () {
                let cmp = this;
                let opts = {
                    type: 'get',
                    url: '/system/dev/dummy-data/list',
                    success: function (response) {
                        cmp.seeders = response.seeders;
                        // 국기 정보는 JS region-factory 캐시에서 보강
                        cmp.regions = response.regions.map(function (r) {
                            var regionInfo = get_region(r.id);
                            r.flag16 = regionInfo ? regionInfo.flag16 : null;
                            return r;
                        }).sort(function (a, b) {
                            return a.id - b.id;
                        });
                        cmp.loading = false;
                    },
                    alert_error_msg: function (msg) {
                        cmp.loading = false;
                        alert('시더 목록 로드 실패: ' + msg);
                    },
                };
                $.app.ajax.module.default(opts);
            },

            /**
             * region-select: 현재 선택된 region 객체 반환
             */
            selectedRegionObj: function (paramName) {
                var id = this.paramValues[paramName];
                if (!id) return null;
                return _.find(this.regions, { id: id }) || null;
            },

            /**
             * region-select: 선택 변경 시 ID만 paramValues에 저장
             */
            onRegionSelect: function (region, paramName) {
                this.$set(this.paramValues, paramName, region ? region.id : null);
            },

            /**
             * 파이프라인 노드 클릭 → 시더 선택
             */
            selectSeederByType: function (type) {
                this.selectedType = type;
                this.onSeederChange();
            },

            /**
             * nodeType이 targetType의 선행(조상) 시더인지 판별
             */
            isAncestorOf: function (nodeType, targetType) {
                if (!targetType || nodeType === targetType) return false;
                var map = {};
                this.seeders.forEach(function (s) { map[s.type] = s; });
                var current = map[targetType];
                while (current && current.parent_type) {
                    if (current.parent_type === nodeType) return true;
                    current = map[current.parent_type];
                }
                return false;
            },

            /**
             * 시더 변경 시 파라미터 초기값 세팅
             */
            onSeederChange: function () {
                let seeder = this.selectedSeeder;
                if (!seeder) {
                    this.paramValues = {};
                    return;
                }

                let values = {};
                _.forEach(seeder.parameters, function (param) {
                    values[param.name] = (param.default !== undefined) ? param.default : '';
                });
                this.paramValues = values;
            },

            /**
             * 파라미터 유효성 검증
             */
            validateParams: function () {
                let seeder = this.selectedSeeder;
                if (!seeder) return false;

                for (let i = 0; i < seeder.parameters.length; i++) {
                    let param = seeder.parameters[i];
                    if (param.required) {
                        let val = this.paramValues[param.name];
                        if (val === '' || val === null || val === undefined) {
                            alert(param.label + ' 값을 입력하세요.');
                            return false;
                        }
                    }
                }

                // member_count와 existing_member_ids 교차 검증
                var memberCount = this.paramValues['member_count'];
                var existingIds = this.paramValues['existing_member_ids'];
                var hasMemberCount = (memberCount !== undefined);
                var hasExistingIds = (existingIds !== undefined);
                if (hasMemberCount || hasExistingIds) {
                    var countIsZero = (!memberCount || memberCount === 0 || memberCount === '');
                    var idsIsEmpty = (!existingIds || !Array.isArray(existingIds) || existingIds.length === 0);
                    if (countIsZero && idsIsEmpty) {
                        alert('신규 회원사 수 또는 기존 회원사를 1개 이상 입력/선택하세요.');
                        return false;
                    }
                }

                return true;
            },

            /**
             * 센터별 회원사 목록 로드
             */
            loadMembers: function (regionId) {
                this.membersLoading = true;
                var cmp = this;
                $.app.ajax.module.default({
                    type: 'get',
                    url: '/system/dev/dummy-data/members?region_id=' + regionId,
                    success: function (response) {
                        cmp.members = response.members.map(function (m) {
                            m.display_label = m.nick + ' (' + m.mailbox + ')';
                            return m;
                        });
                        cmp.membersLoading = false;
                    },
                    alert_error_msg: function () {
                        cmp.membersLoading = false;
                    }
                });
            },

            /**
             * 디스패치 → 시더 실행 요청
             */
            dispatch: function () {
                if (!this.validateParams()) return;

                if (!confirm('더미데이터를 생성하시겠습니까?')) return;

                // 진행상태 → 대기중
                this.jobState = 'pending';
                this.totalCount = 0;
                this.processedCount = 0;
                this.errorMessage = null;
                this.activeJobInfo = this.buildJobInfo({
                    seeder_type: this.selectedType,
                    params: this.paramValues,
                });

                let cmp = this;
                // paramValues를 전송용으로 변환 (vue-multiselect 객체 → ID 배열)
                var sendParams = Object.assign({}, cmp.paramValues);
                if (sendParams.existing_member_ids && Array.isArray(sendParams.existing_member_ids)) {
                    sendParams.existing_member_ids = sendParams.existing_member_ids.map(function (m) { return m.id; });
                }
                let opts = {
                    type: 'post',
                    url: '/system/dev/dummy-data/dispatch',
                    data: {
                        '_token': cmp.csrfToken,
                        'type': cmp.selectedType,
                        'params': sendParams,
                    },
                    success: function (response) {
                        cmp.progressId = response.progress_id;
                        cmp.showNewJobForm = false;
                        cmp.startPolling();
                        cmp.loadRecentJobs();
                    },
                    alert_error_msg: function (msg) {
                        cmp.jobState = 'failed';
                        cmp.errorMessage = msg;
                    },
                };
                $.app.ajax.module.default(opts);
            },

            /**
             * 폴링 시작
             */
            startPolling: function () {
                let cmp = this;
                this.pollTimer = setInterval(function () {
                    cmp.pollProgress();
                }, 2000);
            },

            /**
             * 폴링 중지
             */
            stopPolling: function () {
                if (this.pollTimer) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                }
            },

            /**
             * 진행현황 폴링
             */
            pollProgress: function () {
                let cmp = this;
                let opts = {
                    type: 'get',
                    url: '/batch-job-progresses/' + this.progressId,
                    success: function (response) {
                        cmp.totalCount = response.total_count;
                        cmp.processedCount = response.processed_count;
                        cmp.lastPolledAt = cmp.formatTime(new Date());

                        // 작업정보가 없으면 폴링 응답에서 복원
                        if (!cmp.activeJobInfo && response.request_data) {
                            cmp.activeJobInfo = cmp.buildJobInfo(response.request_data);
                        }

                        // 상태별 처리 (PENDING=1, RUNNING=2, COMPLETED=3, FAILED=9)
                        if (response.status === 1) {
                            cmp.jobState = 'pending';
                        } else if (response.status === 2) {
                            cmp.jobState = 'doing';
                        } else if (response.status === 3) {
                            cmp.stopPolling();
                            cmp.jobState = 'done';
                            cmp.loadRecentJobs();
                        } else if (response.status === 9) {
                            cmp.stopPolling();
                            cmp.jobState = 'failed';
                            cmp.errorMessage = response.error_message;
                            cmp.loadRecentJobs();
                        }
                    },
                    alert_error_msg: function (msg) {
                        // 폴링 에러 시에는 계속 시도 (네트워크 일시 장애 등)
                    },
                };
                $.app.ajax.module.background(opts);
            },

            /**
             * 초기화 (다시 생성하기)
             */
            reset: function () {
                this.stopPolling();
                this.jobState = 'ready';
                this.progressId = null;
                this.totalCount = 0;
                this.processedCount = 0;
                this.errorMessage = null;
                this.lastPolledAt = null;
                this.showNewJobForm = false;
                this.activeJobInfo = null;
                this.loadRecentJobs();
            },

            /**
             * 최근 작업 이력 로드 + 활성 작업 자동 복구
             */
            loadRecentJobs: function () {
                let cmp = this;
                let opts = {
                    type: 'get',
                    url: '/batch-job-progresses?job_type=10',
                    success: function (response) {
                        cmp.recentJobs = response.items;

                        // 활성 작업(PENDING=1, RUNNING=2) 자동 복구
                        if (!cmp.pollTimer) {
                            let activeJob = _.find(response.items, function (job) {
                                return job.status === 1 || job.status === 2;
                            });
                            if (activeJob) {
                                cmp.progressId = activeJob.id;
                                cmp.totalCount = activeJob.total_count;
                                cmp.processedCount = activeJob.processed_count;
                                cmp.jobState = activeJob.status === 1 ? 'pending' : 'doing';
                                cmp.startPolling();
                            }
                        }
                    },
                    alert_error_msg: function () {
                        // 이력 로드 실패 시 무시 (메인 기능에 영향 없음)
                    },
                };
                $.app.ajax.module.background(opts);
            },

            /**
             * region_id로 국기 URL 반환
             */
            getRegionFlag: function (region_id) {
                var regionInfo = get_region(region_id);
                return regionInfo ? regionInfo.flag16 : null;
            },

            /**
             * region_id로 센터명 반환
             */
            getRegionName: function (region_id) {
                var regionInfo = get_region(region_id);
                return regionInfo ? regionInfo.name : '센터 #' + region_id;
            },

            /**
             * 상태 라벨
             */
            statusLabel: function (status) {
                var labels = { 1: '대기', 2: '진행중', 3: '완료', 9: '실패' };
                return labels[status] || '알수없음';
            },

            /**
             * 상태 뱃지 CSS 클래스
             */
            statusBadgeClass: function (status) {
                var classes = {
                    1: 'uk-label',
                    2: 'uk-label uk-label-primary',
                    3: 'uk-label uk-label-success',
                    9: 'uk-label uk-label-danger',
                };
                return classes[status] || 'uk-label';
            },

            /**
             * 시각 포맷 (HH:MM:SS)
             */
            formatTime: function (date) {
                var h = ('0' + date.getHours()).slice(-2);
                var m = ('0' + date.getMinutes()).slice(-2);
                var s = ('0' + date.getSeconds()).slice(-2);
                return h + ':' + m + ':' + s;
            },

            /**
             * 상품ID 배열로 배송조회 URL 생성
             */
            buildProdsUrl: function (prodIds) {
                var params = prodIds.map(function (id) {
                    return 'ids[]=' + id;
                });
                return '/admin/prods?' + params.join('&');
            },

            /**
             * request_data로부터 작업정보 객체 생성
             */
            buildJobInfo: function (requestData) {
                if (!requestData) return null;

                var seederType = requestData.seeder_type || '';
                var params = requestData.params || {};
                var seeder = _.find(this.seeders, { type: seederType });
                var label = seeder ? seeder.label : seederType;

                // 모든 파라미터 빌드 (빈 값도 포함)
                var paramItems = [];
                if (seeder && seeder.parameters) {
                    _.forEach(seeder.parameters, function (paramDef) {
                        var val = params[paramDef.name];
                        var isEmpty = (val === '' || val === null || val === undefined);

                        if (paramDef.type === 'region-select') {
                            var regionInfo = isEmpty ? null : get_region(val);
                            paramItems.push({
                                label: paramDef.label,
                                value: regionInfo ? regionInfo.name : (isEmpty ? '' : '센터 #' + val),
                                flag: regionInfo ? regionInfo.flag16 : null,
                            });
                        } else if (paramDef.type === 'member-select') {
                            var memberVal = '';
                            if (Array.isArray(val) && val.length > 0) {
                                // 객체 배열 또는 ID 배열 모두 처리
                                memberVal = val.map(function (m) {
                                    return typeof m === 'object' ? m.nick : ('#' + m);
                                }).join(', ');
                                memberVal += ' (' + val.length + '명)';
                            }
                            paramItems.push({
                                label: paramDef.label,
                                value: memberVal,
                                flag: null,
                            });
                        } else if (paramDef.type === 'select') {
                            var selectLabel = val;
                            if (paramDef.options && Array.isArray(paramDef.options)) {
                                var matched = _.find(paramDef.options, { value: val });
                                if (matched) selectLabel = matched.label;
                            }
                            paramItems.push({
                                label: paramDef.label,
                                value: isEmpty ? '' : selectLabel,
                                flag: null,
                            });
                        } else {
                            paramItems.push({
                                label: paramDef.label,
                                value: isEmpty ? '' : val,
                                flag: null,
                            });
                        }
                    });
                }

                return { label: label, params: paramItems };
            },
        },
        mounted: function () {
            this.loadSeeders();
            this.loadRecentJobs();
        },
        beforeDestroy: function () {
            this.stopPolling();
        },
    }
</script>

<style lang="scss" scoped>
.dummy-data-wrap {
    margin: 0 auto;
}

/* ── 아코디언 (주요 개념) ── */
.glossary-accordion {
    margin-bottom: 20px;
    padding: 0;
    list-style: none;

    > li {
        background: #f8f9fa;
        border: 1px solid #e5e5e5;
        border-radius: 4px;
    }

    .uk-accordion-title {
        font-size: 1rem;
        color: #666;
        padding: 10px 14px;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .accordion-toggle-icon {
        color: #999;
        transition: transform 0.2s ease;
    }

    > li.uk-open .accordion-toggle-icon {
        transform: rotate(90deg);
    }

    .uk-accordion-content {
        padding: 0 14px 14px;
        margin-top: 0;
    }
}

.glossary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 1rem;
    line-height: 1.5;

    th {
        text-align: left;
        white-space: nowrap;
        padding: 3px 12px 3px 0;
        color: #333;
        font-weight: 600;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 1rem;
        vertical-align: top;
    }

    td {
        padding: 3px 0;
        color: #666;

        code {
            background: #e8ecef;
            padding: 1px 5px;
            border-radius: 3px;
            font-size: 1rem;
            color: #555;
        }
    }
}

.glossary-flow {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e5e5e5;
    text-align: center;
    font-size: 1rem;
    color: #555;
    font-weight: 500;

    .flow-arrow {
        color: #999;
        margin: 0 2px;
    }
}

.glossary-doc-link {
    margin: 8px 0 0;
    font-size: 1rem;
    color: #999;
    text-align: right;

    code {
        background: #e8ecef;
        padding: 1px 5px;
        border-radius: 3px;
        font-size: 1rem;
        color: #555;
    }
}

/* ── 메인 카드 ── */
.main-card {
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* ── 폼 영역 ── */
.form-content {
    margin: 0;
}

.form-group {
    margin-bottom: 14px;
}

.form-label {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 5px;
    letter-spacing: 0.2px;
}

.required-mark {
    color: #e74c3c;
    margin-left: 1px;
}

.seeder-description {
    margin-top: 12px;
    padding: 10px 12px;
    background: #f0f7ff;
    border-left: 3px solid #1e87f0;
    border-radius: 3px;
    font-size: 12px;
    color: #444;
    line-height: 1.6;
}

.param-section {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 12px 16px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #eee;
}

.param-section-title {
    grid-column: 1 / -1;
    font-size: 1rem;
    font-weight: 600;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0;
}

.form-action {
    margin-top: 24px;
    display: flex;
    justify-content: center;
}

.btn-generate {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 240px;
    justify-content: center;
    padding: 10px 24px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: #1a73e8;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s;
    letter-spacing: 0.3px;

    &:hover {
        background: #1557b0;
    }
}

/* ── 새 작업 추가 ── */
.new-job-section {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px dashed #ddd;
}

/* ── 작업 상태 패널 ── */
.job-status-panel {
    max-width: 460px;
    margin: 0 auto;
    border-radius: 8px;
    border: 1px solid #ddd;
    overflow: hidden;

    &.job-status-pending {
        border-color: #b8d4f0;

        .job-panel-header { background: #f0f7ff; }
        .job-state-badge.state-pending { background: #1a73e8; }
    }

    &.job-status-doing {
        border-color: #b8d4f0;

        .job-panel-header { background: #f0f7ff; }
        .job-state-badge.state-doing { background: #1a73e8; }
    }

    &.job-status-done {
        border-color: #b3dcc6;

        .job-panel-header { background: #f0faf4; }
        .job-state-badge.state-done { background: #369a6e; }
    }

    &.job-status-failed {
        border-color: #f0c0c0;

        .job-panel-header { background: #fef5f5; }
        .job-state-badge.state-failed { background: #d44; }
    }
}

.job-panel-header {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid #e8e8e8;
}

.job-state-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.3px;
    flex-shrink: 0;

    .badge-icon {
        font-size: 16px;
    }
}

.job-panel-seeder {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
}

/* ── 파라미터 상세 테이블 ── */
.job-panel-params {
    padding: 10px 16px 6px;
}

.job-params-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 1rem;

    th, td {
        padding: 4px 0;
        vertical-align: middle;
    }

    th {
        text-align: left;
        color: #888;
        font-weight: 500;
        white-space: nowrap;
        width: 1%;
        padding-right: 12px;
    }

    td {
        color: #333;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .param-empty {
        color: #bbb;
    }
}

.job-panel-body {
    padding: 12px 16px 16px;
}

/* ── 진행률 표시 ── */
.progress-section {
    margin-bottom: 12px;
}

.progress-count {
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
}

.progress-count-sm {
    font-size: 1rem;
    color: #888;
    font-weight: 500;
}

.progress-current {
    color: #1a73e8;
}

.progress-divider {
    color: #ccc;
    margin: 0 2px;
}

.progress-total {
    color: #666;
}

.progress-pct {
    font-size: 1rem;
    color: #999;
    font-weight: 500;
    margin-left: 4px;
}

.progress-bar {
    height: 6px;
    border-radius: 3px;
    margin: 0;
}

.job-status-msg {
    font-size: 1rem;
    color: #999;
    margin: 8px 0 0;
}

.error-msg {
    font-size: 1rem;
    color: #c44;
    word-break: break-all;
    margin: 0 0 12px;
    line-height: 1.5;
}

.reset-btn {
    margin-top: 4px;
    font-size: 1rem;
    border-radius: 4px;
}

.poll-timestamp {
    font-size: 1rem;
    color: #bbb;
    text-align: center;
    padding: 0 16px 10px;
    margin: 0;
}

/* ── 이력 카드 ── */
.history-card {
    margin-top: 24px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}

.history-header {
    padding: 14px 20px;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    gap: 8px;
}

.history-title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
}

.history-count {
    font-size: 1rem;
    color: #888;
    background: #f0f0f0;
    padding: 1px 8px;
    border-radius: 10px;
}

.history-body {
    padding: 0 8px;
}

.history-table {
    thead th {
        font-size: 1rem;
        font-weight: 600;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        border-bottom: 1px solid #eee;
    }

    tbody tr {
        transition: background 0.15s;

        &:hover {
            background: #fafbfc;
        }
    }
}

/* ── 이력 테이블 내 링크/뱃지 ── */
.request-region {
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.result-user-link,
.result-house-link {
    color: #0f7ae5;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

/* ── 센터 선택 셀렉트 ── */
.region-select-wrap {
    margin-top: 4px;
}

.region-flag {
    width: 16px;
    height: 16px;
    vertical-align: middle;
    margin-right: 4px;
}

/* ── 파이프라인 시더 선택 ── */
.pipeline-wrap {
    margin-bottom: 16px;
}

.pipeline-label {
    font-size: 1rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 8px;
}

.pipeline-scenario {
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
}

.pipeline-scenario-label {
    font-size: 1rem;
    color: #888;
    margin-bottom: 4px;
    padding-left: 2px;
}

.pipeline {
    display: flex;
    align-items: stretch;
    gap: 0;
    overflow-x: auto;
    padding-bottom: 4px;
}

.pipeline-node {
    position: relative;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 14px 8px 10px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    cursor: pointer;
    white-space: nowrap;
    font-size: 1rem;
    color: #666;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    user-select: none;

    &:first-child {
        border-radius: 4px 0 0 4px;
    }

    &:last-child {
        border-radius: 0 4px 4px 0;
    }

    &:not(:first-child) {
        border-left: none;
    }

    &:hover {
        background: #eef4fb;
        border-color: #b0c8e8;
        color: #333;
    }

    &.pipeline-ancestor {
        background: #e8f0fe;
        border-color: #a8c7f0;
        color: #1a5fb4;
    }

    &.pipeline-selected {
        background: #1a73e8;
        border-color: #1a73e8;
        color: #fff;

        .pipeline-step {
            background: #fff;
            color: #1a73e8;
        }
    }
}

.pipeline-step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ccc;
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    flex-shrink: 0;

    .pipeline-ancestor & {
        background: #1a73e8;
    }
}

.pipeline-node-label {
    font-weight: 500;
}

/* ── 시더별 파라미터 그룹 ── */
.param-group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 260px));
    gap: 10px 16px;
    margin-bottom: 0;
    align-self: start;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 12px 14px;

    > .form-group {
        margin-bottom: 0;
    }

    > .form-group-wide {
        grid-column: 1 / -1;
    }

    &.param-group-empty {
        display: flex;
        align-items: center;
        padding: 8px 14px;
        background: #fafafa;
        border-color: #eee;
    }
}

.param-group-header {
    grid-column: 1 / -1;
    font-size: 1rem;
    font-weight: 600;
    color: #666;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.param-group-auto {
    font-size: 11px;
    font-weight: 500;
    color: #999;
    background: #eee;
    padding: 1px 8px;
    border-radius: 3px;
    margin-left: 4px;
}

.param-group-number {
    background: #1a73e8;
    color: #fff;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
}

/* ── 회원사 셀렉트 ── */
.member-select-wrap {
    margin-top: 4px;
}

.member-option-detail {
    color: #888;
    margin-left: 4px;
    font-size: 1rem;
}

.member-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #f0f7ff;
    border: 1px solid #d0e3f7;
    border-radius: 3px;
    padding: 2px 6px;
    margin: 2px 4px 2px 0;
    font-size: 1rem;
    line-height: 1.4;
}

.member-tag-payment {
    font-size: 1rem;

    &.payment-card {
        color: #1a73e8;
    }

    &.payment-deposit {
        color: #888;
    }
}

.member-tag-remove {
    cursor: pointer;
    color: #999;
    margin-left: 2px;
    font-weight: bold;

    &:hover {
        color: #c44;
    }
}

.payment-badge-card {
    color: #1a73e8;
    font-weight: 600;
}

.payment-badge-deposit {
    color: #888;
}
</style>
